import { mapGetters } from 'vuex'

export const mobileInputScroll = {
  computed: {
    ...mapGetters('app', ['currentBreakPoint']),

    isDesktopBreakpoint() {
      return this.currentBreakPoint === 'lg' || this.currentBreakPoint === 'xl'
    }
  },

  mounted() {
    this.setupMobileInputScroll()
  },

  beforeDestroy() {
    this.cleanupMobileInputScroll()
  },

  methods: {
    setupMobileInputScroll() {
      // Only apply on mobile devices
      if (this.isDesktopBreakpoint) {
        return
      }

      // Add event listeners for various input types
      this.addInputListeners()
    },

    cleanupMobileInputScroll() {
      // Remove event listeners
      if (this.inputScrollHandlers) {
        this.inputScrollHandlers.forEach(({ element, events }) => {
          events.forEach(({ event, handler }) => {
            element.removeEventListener(event, handler)
          })
        })
        this.inputScrollHandlers = []
      }
    },


    addInputListeners() {
      this.inputScrollHandlers = []

      // Wait for DOM to be ready
      this.$nextTick(() => {
        // Find all input elements that might need scrolling
        const selectors = [
          'input[type="text"]',
          'input[type="email"]',
          'input[type="password"]',
          'input[type="search"]',
          'input[type="tel"]',
          'input[type="url"]',
          'input[type="number"]',
          'textarea',
          '.form-control',
          '.vs__search',
          '.vs__selected',
          '.vue-autosuggest__input',
          '.filepond--file',
          '.quill-editor .ql-editor'
        ]

        selectors.forEach(selector => {
          const elements = this.$el.querySelectorAll(selector)
          elements.forEach(element => {
            this.addElementListeners(element)
          })
        })

        // Also listen for dynamically added elements
        this.observeDynamicElements()
      })
    },

    addElementListeners(element) {
      if (!element || element.dataset.mobileScrollSetup) {
        return
      }

      // Mark as setup to avoid duplicates
      element.dataset.mobileScrollSetup = 'true'

      const events = [
        { event: 'focus', handler: (e) => this.handleInputFocus(e) },
        { event: 'click', handler: (e) => this.handleInputClick(e) },
        { event: 'touchstart', handler: (e) => this.handleInputTouch(e) }
      ]

      events.forEach(({ event, handler }) => {
        element.addEventListener(event, handler, { passive: true })
      })

      this.inputScrollHandlers = this.inputScrollHandlers || []
      this.inputScrollHandlers.push({ element, events })
    },

    observeDynamicElements() {
      // Use MutationObserver to watch for dynamically added elements
      if (this.mutationObserver) {
        this.mutationObserver.disconnect()
      }

      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if it's an input element or contains input elements
              const selectors = [
                'input[type="text"]',
                'input[type="email"]',
                'input[type="password"]',
                'input[type="search"]',
                'input[type="tel"]',
                'input[type="url"]',
                'input[type="number"]',
                'textarea',
                '.form-control',
                '.vs__search',
                '.vs__selected',
                '.vue-autosuggest__input',
                '.filepond--file',
                '.quill-editor .ql-editor'
              ]

              selectors.forEach(selector => {
                if (node.matches && node.matches(selector)) {
                  this.addElementListeners(node)
                }
                const childElements = node.querySelectorAll && node.querySelectorAll(selector)
                if (childElements) {
                  childElements.forEach(child => this.addElementListeners(child))
                }
              })
            }
          })
        })
      })

      this.mutationObserver.observe(this.$el, {
        childList: true,
        subtree: true
      })
    },

    handleInputFocus(event) {
      this.scrollToInput(event.target)
    },

    handleInputClick(event) {
      // Small delay to ensure dropdown/autosuggest is rendered
      setTimeout(() => {
        this.scrollToInput(event.target)
      }, 100)
    },

    handleInputTouch(event) {
      // For touch devices, scroll immediately on touch
      this.scrollToInput(event.target)
    },

    scrollToInput(element) {
      if (!element || this.isDesktopBreakpoint) {
        return
      }

      // Get the element's position
      const rect = element.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth

      // Calculate if element is visible
      const isVisible = rect.top >= 0 &&
                       rect.left >= 0 &&
                       rect.bottom <= viewportHeight &&
                       rect.right <= viewportWidth

      // If element is not fully visible, scroll to it
      if (!isVisible) {
        // Calculate scroll position
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const elementTop = rect.top + scrollTop
        const elementHeight = rect.height

        // Add some padding above the element (20% of viewport height)
        const padding = viewportHeight * 0.2
        const targetScrollTop = elementTop - padding

        // Smooth scroll to the element
        window.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        })

        // For dropdowns/autosuggests, also scroll horizontally if needed
        if (rect.right > viewportWidth) {
          window.scrollTo({
            left: rect.left - 20,
            behavior: 'smooth'
          })
        }
      }
    },

    // Public method to manually scroll to an element
    scrollToElement(selector) {
      const element = this.$el.querySelector(selector)
      if (element) {
        this.scrollToInput(element)
      }
    }
  },

  data() {
    return {
      inputScrollHandlers: [],
      mutationObserver: null
    }
  }
}
